import { useState, type ReactElement } from 'react';
import { RevealPanel } from '@/components/KeyCard';

interface RevealApiKeyDialogProps {
  /** The one-time plaintext access key. Never persisted anywhere. */
  accessKey: string;
  /** Called once the user has acknowledged and dismissed the panel. */
  onClose: () => void;
}

/**
 * One-time reveal of a freshly minted management API key (KIR-3748).
 *
 * Two deliberate choices:
 *
 * - **Reuses `RevealPanel`** from `KeyCard` rather than re-implementing copy,
 *   download, and the acknowledgement checkbox, so both key surfaces make the
 *   same promise to the user in the same words.
 * - **Not `ui/dialog`.** A plain fixed overlay, exactly as `KeyRollModal` does
 *   it, because Radix's dialog closes on Esc and on a click outside. For a
 *   secret that can never be shown again, an accidental dismissal is data loss;
 *   the only way out of this panel is the acknowledged "Done" button.
 */
export function RevealApiKeyDialog({
  accessKey,
  onClose,
}: RevealApiKeyDialogProps): ReactElement {
  const [ack, setAck] = useState(false);

  return (
    <div
      className="fixed inset-0 bg-black/50 grid place-items-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Your new management API key"
    >
      <div className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-xl p-6 shadow-xl">
        <RevealPanel
          plaintext={accessKey}
          ack={ack}
          setAck={setAck}
          onClose={onClose}
        />
      </div>
    </div>
  );
}
